import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { App } from 'antd';
import { clearSystemAccess, readSystemAccess } from '@/utils/systemAccess';
import { clearExpiredLogin, redirectToLogin } from '@/utils/loginSession';

// 获取全局 message 和 notification 实例
const getMessageApi = () => {
  if (typeof window !== 'undefined' && (window as any).__ANTD_MESSAGE__) {
    return (window as any).__ANTD_MESSAGE__;
  }
  // 如果 App context 未初始化，返回一个空实现
  return {
    warning: console.warn,
    error: console.error,
    success: console.log,
  };
};

const getNotificationApi = () => {
  if (typeof window !== 'undefined' && (window as any).__ANTD_NOTIFICATION__) {
    return (window as any).__ANTD_NOTIFICATION__;
  }
  // 如果 App context 未初始化，返回一个空实现
  return {
    open: console.log,
  };
};

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { success, data, errorCode, errorMessage, showType } =
        res as unknown as ResponseStructure;
      if (!success) {
        const error: any = new Error(errorMessage);
        error.name = 'BizError';
        error.info = { errorCode, errorMessage, showType, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // The response interceptor already handles revocation, including ignoring
      // late responses for an earlier login. Do not clear a newer token here.
      if (error.response?.data?.code === 'SESSION_INVALIDATED') return;
      
      // 使用静态方法，避免在错误处理中使用 Hook
      
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              getMessageApi().warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              getMessageApi().error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              getNotificationApi().open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              getMessageApi().error(errorMessage);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        if (error.response.status === 401) {
          // 登录/修改密码等认证接口的401属于业务错误，不做登出处理
          const requestUrl = error.response.config?.url || '';
          const authPaths = ['/api/v1/auth/login', '/api/v1/auth/change-password'];
          const isAuthRequest = authPaths.some(path => requestUrl.includes(path));
          if (!isAuthRequest) {
            getMessageApi().error('登录已过期，请重新登录');
            localStorage.removeItem('token');
            if (window.location.pathname !== '/user/login') {
              window.location.href = '/user/login';
            }
            return;
          }
        }
        // 尝试从响应中提取错误消息
        const errorMessage = error.response.data?.message ||
                            error.response.data?.error ||
                            error.response.data?.errorMessage ||
                            `Response status:${error.response.status}`;
        getMessageApi().error(errorMessage);
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        getMessageApi().error('None response! Please retry.');
      } else {
        // 发送请求时出了点问题
        getMessageApi().error('Request error, please retry.');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // 拦截请求配置，进行个性化处理。
      // 自动添加 Authorization header
      const token = localStorage.getItem('token');
      if (token && !config.headers?.['Authorization']) {
        config.headers = {
          ...config.headers,
          'Authorization': `Bearer ${token}`,
        };
      }
      const verification = readSystemAccess();
      const apiBase = process.env.NODE_ENV === 'development' ? 'http://localhost:9000' : (process.env.API_BASE_URL || window.location.origin);
      const target = new URL(config.url || '', new URL(config.baseURL || '/', window.location.origin));
      // Match the actual login identity; never forward this proof to third-party URLs.
      const isOwnAPI = target.pathname.startsWith('/api/') &&
        (target.origin === window.location.origin || target.origin === new URL(apiBase, window.location.origin).origin);
      if (verification && isOwnAPI && config.headers?.['Authorization'] === `Bearer ${verification.loginToken}`) {
        config.headers = { ...config.headers, 'X-System-Verification': verification.token };
      }
      return config;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    [response => response, (error: any) => {
      if ((error.response?.status === 401 && error.response.data?.code === 'SESSION_INVALIDATED') ||
          (error.response?.data?.code === 'SYSTEM_PASSWORD_INVALID' && error.response.data?.logout === true)) {
        const headers = error.response.config?.headers || error.config?.headers;
        const authorization = headers?.get?.('Authorization') || headers?.Authorization || headers?.authorization;
        if (clearExpiredLogin(authorization)) redirectToLogin();
      }
      const code = error.response?.data?.code;
      if (code === 'SYSTEM_VERIFICATION_REQUIRED' || code === 'SYSTEM_PASSWORD_INVALID') {
        const headers = error.response.config?.headers || error.config?.headers;
        const proof = headers?.get?.('X-System-Verification') || headers?.['X-System-Verification'];
        // A late failure from a previous page must not lock the new page.
        if (proof && proof === readSystemAccess()?.token) {
          clearSystemAccess(code === 'SYSTEM_PASSWORD_INVALID' ? '密码长度错误' : '');
        }
      }
      return Promise.reject(error);
    }],
    (response) => {
      // 拦截响应数据，进行个性化处理
      const { data } = response as unknown as ResponseStructure;

      if (data?.success === false) {
        // 使用全局 message 实例
        getMessageApi().error('请求失败！');
      }
      return response;
    },
  ],
};
